import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type SubmitHandler, type SubmitErrorHandler } from 'react-hook-form';
import { typedResolver } from '@/lib/typedResolver';
import { toast } from 'sonner';
import { getErrorMessage } from '@marine/utils/getErrorMessage';
import { ChevronRight } from 'lucide-react';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Textarea } from '@marine/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@marine/ui/select';
import { Checkbox } from '@marine/ui/checkbox';
import { Separator } from '@marine/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@marine/ui/collapsible';
import { useCreateLead, useUpdateLead } from '../hooks/useLeads';
import { usePipelines } from '../hooks/usePipelines';
import { CustomerSearchCombobox } from './CustomerSearchCombobox';
import { QuickCreateCustomerDialog } from './QuickCreateCustomerDialog';
import { InventoryBoatCombobox } from './InventoryBoatCombobox';
import { ManufacturerCombobox } from '@/components/combobox/ManufacturerCombobox';
import { leadFormSchema } from '../validators/leadSchema';
import type { Customer, Lead } from '../types/crm.types';
import type { LeadFormData } from '../validators/leadSchema';
import { useLocations } from '@/modules/inventory/hooks/useLocations';
import { useEmployee } from '@/modules/tenant/hooks/useTenantAuth';

interface LeadFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead?: Lead;
  /** Seed values for a NEW deal (#562) — ignored when editing. Snapshotted when
      the sheet opens (#598), so later identity changes never re-run the reset
      or wipe in-progress input. */
  defaults?: Partial<
    Pick<
      LeadFormData,
      | 'customer_id'
      | 'title'
      | 'description'
      | 'boat_type_interest'
      | 'estimated_value'
      | 'source_enquiry_id'
      | 'manufacturer_id'
    >
  >;
  /** Resolves the customer combobox label when defaults.customer_id isn't in the first page of results. */
  initialCustomer?: Customer;
  onCreated?: (id: string) => void;
}

export function LeadFormSheet({ open, onOpenChange, lead, defaults, initialCustomer, onCreated }: LeadFormSheetProps) {
  const isEditing = !!lead;
  // #598 — the open-reset reads the ref, not the prop: the seed is whatever
  // `defaults` holds at the moment the sheet opens, and a parent re-render
  // with a fresh object identity can't re-fire the reset mid-edit.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showCoBuyerQuickCreate, setShowCoBuyerQuickCreate] = useState(false);
  
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const { data: pipelinesData } = usePipelines();
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();
  const employee = useEmployee();
  
  const pipelines = pipelinesData?.items || [];
  const locations = useMemo(() => locationsData ?? [], [locationsData]);
  const defaultLocationId = useMemo(() => {
    if (employee?.location_id) {
      return employee.location_id;
    }
    const primary = locations.find((location: { id: string; name: string; is_main?: boolean }) => location.is_main) || locations[0];
    return primary?.id || '';
  }, [employee?.location_id, locations]);

  const form = useForm<LeadFormData>({
    resolver: typedResolver(leadFormSchema),
    defaultValues: {
      customer_id: '',
      has_co_buyer: false,
      co_buyer_customer_id: '',
      title: '',
      pipeline_id: '',
      stage_id: '',
      has_trade_in: false,
      score: 50,
      location_id: '',
    },
  });
  
  const selectedPipelineId = form.watch('pipeline_id');
  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
  const stages = selectedPipeline?.stages || [];
  
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (lead) {
        form.reset({
          customer_id: lead.customer_id,
          has_co_buyer: lead.has_co_buyer ?? !!lead.co_buyer_customer_id,
          co_buyer_customer_id: lead.co_buyer_customer_id || '',
          title: lead.title,
          description: lead.description || '',
          lead_source: lead.lead_source || '',
          location_id: lead.location_id || defaultLocationId || '',
          pipeline_id: lead.pipeline_id,
          stage_id: lead.stage_id,
          boat_id: lead.boat_id ?? undefined,
          manufacturer_id: lead.manufacturer_id ?? null,
          boat_type_interest: lead.boat_type_interest || '',
          budget_min: lead.budget_min,
          budget_max: lead.budget_max,
          assigned_to: lead.assigned_to ?? undefined,
          score: lead.score || 50,
          estimated_value: lead.estimated_value,
          expected_close_date: lead.expected_close_date || '',
          next_follow_up_date: lead.next_follow_up_date || '',
          has_trade_in: lead.has_trade_in || false,
        });
      } else {
        form.reset({
          customer_id: '',
          has_co_buyer: false,
          co_buyer_customer_id: '',
          title: '',
          pipeline_id: '',
          stage_id: '',
          has_trade_in: false,
          score: 50,
          location_id: defaultLocationId || '',
          manufacturer_id: null,
          ...defaultsRef.current,
        });
      }
    }
  }, [open, lead, form, defaultLocationId]);

  useEffect(() => {
    if (!open || lead) {
      return;
    }
    if (!defaultLocationId) {
      return;
    }
    if (!form.getValues('location_id')) {
      form.setValue('location_id', defaultLocationId, { shouldDirty: true });
    }
  }, [open, lead, defaultLocationId, form]);
  
  const handleQuickCreateSuccess = (customerId: string) => {
    form.setValue('customer_id', customerId, { 
      shouldValidate: true,
      shouldDirty: true 
    });
    form.clearErrors('customer_id');
  };

  const handleCoBuyerQuickCreateSuccess = (customerId: string) => {
    form.setValue('co_buyer_customer_id', customerId, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.clearErrors('co_buyer_customer_id');
  };
  
  const onSubmit: SubmitHandler<LeadFormData> = async (data) => {
    const payload: LeadFormData = {
      ...data,
      co_buyer_customer_id: data.has_co_buyer ? data.co_buyer_customer_id : undefined,
    };

    try {
      if (isEditing && lead) {
        await updateLead.mutateAsync({ id: lead.id, data: payload });
        toast.success('Deal updated successfully');
      } else {
        const created = await createLead.mutateAsync(payload);
        toast.success('Deal created successfully');
        if (created?.id) onCreated?.(String(created.id));
      }
      onOpenChange(false);
      form.reset();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to save lead'));
    }
  };
  
  const onInvalid: SubmitErrorHandler<LeadFormData> = () => {
    toast.error('Please fix the highlighted fields.');
  };
  
  const isPending = createLead.isPending || updateLead.isPending;
  const hasCoBuyer = form.watch('has_co_buyer');
  
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent variant="panel" className="lg:w-[900px]">
          {/* Header */}
          <SheetHeader className="border-b py-3 px-5 border-border">
            <SheetTitle className="text-sm font-medium">
              {isEditing ? 'Edit Deal' : 'Create New Deal'}
            </SheetTitle>
          </SheetHeader>

          {/* Body */}
          {/* The form is part of the sheet's vertical flex chain: without
              flex/min-h-0 here it is a plain block, SheetBody's flex-1 has
              nothing to measure against and the body cannot scroll. This is
              what the old h-[calc(100dvh-10rem)] was really working around. */}
          <form
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            className="flex flex-1 flex-col min-h-0"
          >
            <SheetBody className="px-1.5 py-0">
                <div className="px-5 py-5 space-y-5">
                  <div className="space-y-5">
                    <div className="text-sm font-semibold">Customer</div>
                      {/* Customer Selection */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                          Customer <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex-1 space-y-1">
                          <CustomerSearchCombobox
                            value={form.watch('customer_id')}
                            onChange={(id) =>
                              form.setValue('customer_id', id ?? '', {
                                shouldValidate: true,
                                shouldDirty: true,
                              })
                            }
                            onQuickCreate={() => setShowQuickCreate(true)}
                            initialCustomer={initialCustomer}
                          />
                          {form.formState.errors.customer_id && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.customer_id.message}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Co-buyer */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Co-buyer</Label>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="has_co_buyer"
                              checked={hasCoBuyer}
                              onCheckedChange={(checked) => {
                                const isChecked = checked === true;
                                form.setValue('has_co_buyer', isChecked, { shouldDirty: true });
                                if (!isChecked) {
                                  form.setValue('co_buyer_customer_id', '', { shouldDirty: true });
                                  form.clearErrors('co_buyer_customer_id');
                                }
                              }}
                            />
                            <label
                              htmlFor="has_co_buyer"
                              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              This lead has a co-buyer
                            </label>
                          </div>
                          {hasCoBuyer && (
                            <div className="space-y-1">
                              <CustomerSearchCombobox
                                value={form.watch('co_buyer_customer_id')}
                                onChange={(id) => form.setValue('co_buyer_customer_id', id || '')}
                                onQuickCreate={() => setShowCoBuyerQuickCreate(true)}
                                placeholder="Search co-buyer..."
                              />
                              {form.formState.errors.co_buyer_customer_id && (
                                <p className="text-xs text-destructive">
                                  {form.formState.errors.co_buyer_customer_id.message}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Deal Title */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                          Deal Title <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex-1 space-y-1">
                          <Input
                            placeholder="e.g., 2024 Boston Whaler Purchase Inquiry"
                            {...form.register('title')}
                          />
                          {form.formState.errors.title && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.title.message}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Enquiry Notes */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Inquiry Notes</Label>
                        <Textarea
                          placeholder="Deal details..."
                          {...form.register('description')}
                          rows={2}
                          className="flex-1 min-h-[56px]"
                        />
                      </div>

                      {/* Lead Source */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Deal Source</Label>
                        <Input
                          placeholder="e.g., Website, Referral, Walk-in"
                          {...form.register('lead_source')}
                          className="flex-1"
                        />
                      </div>

                      {/* Location */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                          Location <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex-1 space-y-1">
                          <Select
                            value={form.watch('location_id') || ''}
                            onValueChange={(value) => form.setValue('location_id', value, { shouldDirty: true })}
                            disabled={isLoadingLocations}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={isLoadingLocations ? 'Loading locations...' : 'Select location'} />
                            </SelectTrigger>
                            <SelectContent>
                              {locations.map((location: { id: string; name: string; is_main?: boolean }) => (
                                <SelectItem key={location.id} value={location.id}>
                                  {location.name}
                                  {location.is_main ? ' • Primary' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {form.formState.errors.location_id && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.location_id.message}
                            </p>
                          )}
                        </div>
                      </div>
                  </div>

                  <Separator />

                  <div className="space-y-5">
                    <div className="text-sm font-semibold">Pipeline</div>
                      {/* Pipeline */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                          Pipeline <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex-1 space-y-1">
                          <Select
                            value={form.watch('pipeline_id')}
                            onValueChange={(value) => {
                              form.setValue('pipeline_id', value);
                              form.setValue('stage_id', ''); // Reset stage when pipeline changes
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select pipeline" />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelines.map((pipeline) => (
                                <SelectItem key={pipeline.id} value={pipeline.id}>
                                  {pipeline.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {form.formState.errors.pipeline_id && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.pipeline_id.message}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Stage */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                          Current Status <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex-1 space-y-1">
                          <Select
                            value={form.watch('stage_id')}
                            onValueChange={(value) => form.setValue('stage_id', value)}
                            disabled={!selectedPipelineId}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={selectedPipelineId ? "Select stage" : "Select pipeline first"} />
                            </SelectTrigger>
                            <SelectContent>
                              {stages.map((stage) => (
                                <SelectItem key={stage.id} value={stage.id}>
                                  {stage.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {form.formState.errors.stage_id && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.stage_id.message}
                            </p>
                          )}
                        </div>
                      </div>
                  </div>

                  <Separator />

                  <Collapsible defaultOpen className="space-y-4">
                    <div className="flex items-center justify-between">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                        >
                          <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                          Boat Interest
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="space-y-5 pt-2">
                      {/* Specific Boat from Inventory */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Specific Boat</Label>
                        <div className="flex-1">
                          <InventoryBoatCombobox
                            value={form.watch('boat_id')}
                            onChange={(id) =>
                              form.setValue('boat_id', id ?? undefined, { shouldDirty: true })
                            }
                          />
                          <p className="text-xs text-muted-foreground mt-1">Select from inventory</p>
                        </div>
                      </div>

                      {/* Brand interest (#706) */}
                      <div className="flex items-start gap-10">
                        <Label htmlFor="manufacturer_id" className="text-xs font-medium w-32 shrink-0 pt-2">Brand</Label>
                        <div className="flex-1">
                          <ManufacturerCombobox
                            id="manufacturer_id"
                            value={form.watch('manufacturer_id')}
                            onChange={(id) =>
                              form.setValue('manufacturer_id', id, { shouldDirty: true })
                            }
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Routes to a brand specialist once auto-assignment is on
                          </p>
                        </div>
                      </div>

                      {/* Boat Type Interest */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Boat Type Interest</Label>
                        <Input
                          placeholder="e.g., Boston Whaler, Sea Ray"
                          {...form.register('boat_type_interest')}
                          className="flex-1"
                        />
                      </div>

                      {/* Budget Range */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Budget Range</Label>
                        <div className="flex-1 flex gap-3">
                          <Input
                            type="number"
                            placeholder="Min"
                            {...form.register('budget_min', { valueAsNumber: true })}
                          />
                          <Input
                            type="number"
                            placeholder="Max"
                            {...form.register('budget_max', { valueAsNumber: true })}
                          />
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  {/* #562 — starts open when a create-deal prefill seeded a value here
                      (e.g. Aimbase MSRP), so the user sees what got carried over instead
                      of it sitting invisibly behind a collapsed section. */}
                  <Collapsible defaultOpen={!!defaults?.estimated_value} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                        >
                          <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                          Value & Timing
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="space-y-5 pt-2">
                      {/* Estimated Sale Value */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Estimated Sale Value</Label>
                        <div className="flex-1 space-y-1">
                          <Input
                            type="number"
                            placeholder="0.00"
                            {...form.register('estimated_value', { valueAsNumber: true })}
                          />
                          <p className="text-xs text-muted-foreground">Leave blank if unknown</p>
                        </div>
                      </div>

                      {/* Expected Close Date */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Expected Purchase Date</Label>
                        <Input
                          type="date"
                          {...form.register('expected_close_date')}
                          className="flex-1"
                        />
                      </div>

                      {/* Next Follow-up */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Next Follow-Up Date</Label>
                        <Input
                          type="date"
                          {...form.register('next_follow_up_date')}
                          className="flex-1"
                        />
                      </div>

                      {/* Deal Score */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Priority Level</Label>
                        <div className="flex-1">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            {...form.register('score', { valueAsNumber: true })}
                            className="w-32"
                          />
                          <p className="text-xs text-muted-foreground mt-1">0-100 score</p>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />

                  <Collapsible defaultOpen={false} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                        >
                          <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                          Trade-In
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="space-y-5 pt-2">
                      {/* Trade-in */}
                      <div className="flex items-start gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0 pt-2">Trade-In</Label>
                        <div className="flex-1 flex items-center space-x-2">
                          <Checkbox
                            id="has_trade_in"
                            checked={form.watch('has_trade_in')}
                            onCheckedChange={(checked) => form.setValue('has_trade_in', checked as boolean)}
                          />
                          <label
                            htmlFor="has_trade_in"
                            className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Customer has boat for trade-in
                          </label>
                        </div>
                      </div>

                      {/* Trade-in fields (conditional) */}
                      {form.watch('has_trade_in') && (
                        <>
                          <div className="flex items-start gap-10">
                            <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                              Trade-in Year <span className="text-destructive">*</span>
                            </Label>
                            <div className="flex-1 space-y-1">
                              <Input
                                type="number"
                                placeholder="e.g., 2018"
                                {...form.register('tradein_year', { valueAsNumber: true })}
                              />
                              {form.formState.errors.tradein_year && (
                                <p className="text-xs text-destructive">
                                  {form.formState.errors.tradein_year.message}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-10">
                            <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                              Trade-in Make <span className="text-destructive">*</span>
                            </Label>
                            <div className="flex-1 space-y-1">
                              <Input
                                placeholder="e.g., Boston Whaler"
                                {...form.register('tradein_make')}
                              />
                              {form.formState.errors.tradein_make && (
                                <p className="text-xs text-destructive">
                                  {form.formState.errors.tradein_make.message}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-10">
                            <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                              Trade-in Model <span className="text-destructive">*</span>
                            </Label>
                            <div className="flex-1 space-y-1">
                              <Input
                                placeholder="e.g., 270 Vantage"
                                {...form.register('tradein_model')}
                              />
                              {form.formState.errors.tradein_model && (
                                <p className="text-xs text-destructive">
                                  {form.formState.errors.tradein_model.message}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-10">
                            <Label className="text-xs font-medium w-32 shrink-0 pt-2">Trade-in Value</Label>
                            <Input
                              type="number"
                              placeholder="0.00"
                              {...form.register('tradein_estimated_value', { valueAsNumber: true })}
                              className="flex-1"
                            />
                          </div>

                          <div className="flex items-start gap-10">
                            <Label className="text-xs font-medium w-32 shrink-0 pt-2">Trade-in Notes</Label>
                            <Textarea
                              placeholder="Condition, notes, or additional details about the trade-in..."
                              {...form.register('tradein_notes')}
                              className="flex-1 min-h-[80px]"
                            />
                          </div>
                        </>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </div>
            </SheetBody>

            {/* Footer */}
            <SheetFooter className="flex-row border-t py-2.5 px-5 border-border gap-2.5">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isPending}>
                {isPending ? 'Saving...' : isEditing ? 'Update Lead' : 'Create Lead'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <QuickCreateCustomerDialog
        open={showQuickCreate}
        onOpenChange={setShowQuickCreate}
        onSuccess={handleQuickCreateSuccess}
      />

      <QuickCreateCustomerDialog
        open={showCoBuyerQuickCreate}
        onOpenChange={setShowCoBuyerQuickCreate}
        onSuccess={handleCoBuyerQuickCreateSuccess}
      />
    </>
  );
}
